import { useCallback, useEffect, useRef, useState } from "react";
import { Domain, getDomain } from "@/utils/domain";
import useUserStore from "@/stores/user.store";
import { logger } from "@/lib/logger";

interface HubSpotFormConfig {
  portalId: string;
  formId: string;
  region: string;
}

const HUBSPOT_SCRIPT_URL = "//js-eu1.hsforms.net/forms/embed/v2.js";

// Form IDs for each tenant
const LARA_FORM_ID = "4228b769-3143-4ac9-9e0d-6ead786fc4df";
const LODA_FORM_ID = "a96482f8-f9cb-40da-9587-5291e9cf3e82";
const PORTAL_ID = "139805471";
const REGION = "eu1";

const getFormConfig = (): HubSpotFormConfig => {
  const domain = getDomain(window.location.hostname);

  const formId = domain === Domain.LODA ? LODA_FORM_ID : LARA_FORM_ID;

  return {
    portalId: PORTAL_ID,
    formId,
    region: REGION,
  };
};

// Read a CSS variable from :root or body (fallback to provided default)
const getCSSVariable = (varName: string, fallback = ""): string => {
  if (typeof window === "undefined") return fallback;

  // Try reading from body first (where .dark class is applied)
  const body = document.getElementById("body") || document.body;
  const bodyValue = getComputedStyle(body).getPropertyValue(varName).trim();
  if (bodyValue) return bodyValue;

  // Fallback to document element
  const rootValue = getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
  return rootValue || fallback;
};

// Normalize a CSS variable value to a valid hsl(...) string
const asHsl = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return trimmed.startsWith("hsl") ? trimmed : `hsl(${trimmed})`;
};

const HubSpotForm = () => {
  const formRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const formCreatedRef = useRef(false);
  const { me } = useUserStore();

  const createForm = useCallback(() => {
    if (!formRef.current || formCreatedRef.current) {
      return;
    }

    try {
      const config = getFormConfig();

      if (!window.hbspt?.forms?.create) {
        setError("HubSpot formular API er ikke tilgængelig.");
        setIsLoading(false);
        return;
      }

      // Hide form container until ready - remove from layout flow
      if (formRef.current) {
        formRef.current.style.display = "none";
      }

      // Inject CSS to prevent button color flash
      const primary = asHsl(getCSSVariable("--primary", "183 100% 22%"));
      const primaryForeground = asHsl(
        getCSSVariable("--primary-foreground", "0 0% 98%")
      );

      // Inject global styles to prevent color flash
      let styleElement = document.getElementById(
        "hubspot-form-preload-styles"
      ) as HTMLStyleElement;
      if (!styleElement) {
        styleElement = document.createElement("style");
        styleElement.id = "hubspot-form-preload-styles";
        document.head.appendChild(styleElement);
      }
      styleElement.textContent = `
        #hubspot-form-container input[type="submit"].hs-button {
          background: ${primary} !important;
          color: ${primaryForeground} !important;
          border-color: transparent !important;
          transition: none !important;
        }
      `;

      window.hbspt.forms.create({
        portalId: config.portalId,
        formId: config.formId,
        region: config.region,
        target: `#hubspot-form-container`,
        onFormReady: (form: HTMLFormElement) => {
          // Detect current theme by checking if body has .dark class
          const body = document.getElementById("body") || document.body;
          const isDark = body?.classList.contains("dark") ?? false;

          // Get theme colors with appropriate fallbacks based on theme
          const foreground = asHsl(
            getCSSVariable("--foreground", isDark ? "0 0% 98%" : "0 0% 3.9%")
          );
          const background = asHsl(
            getCSSVariable("--background", isDark ? "0 0% 10%" : "0 0% 100%")
          );
          const card = asHsl(
            getCSSVariable("--card", isDark ? "0 0% 18%" : "0 0% 100%")
          );
          const border = asHsl(
            getCSSVariable("--border", isDark ? "0 0% 14.9%" : "0 0% 80%")
          );
          const mutedForeground = asHsl(
            getCSSVariable(
              "--muted-foreground",
              isDark ? "0 0% 63.9%" : "0 0% 45.1%"
            )
          );
          const inputFocused = asHsl(
            getCSSVariable("--input-focused", "183 100% 22%")
          );
          const primary = asHsl(getCSSVariable("--primary", "183 100% 22%"));
          const primaryForeground = asHsl(
            getCSSVariable("--primary-foreground", "0 0% 98%")
          );
          const destructive = asHsl(
            getCSSVariable(
              "--destructive",
              isDark ? "0 62.8% 50.6%" : "0 84.2% 60.2%"
            )
          );

          // Pre-fill form fields
          const firstName = form.querySelector<HTMLInputElement>(
            'input[name="firstname"]'
          );
          const email = form.querySelector<HTMLInputElement>(
            'input[name="email"]'
          );
          if (firstName) {
            firstName.value = me?.firstName ?? "";
            firstName.dispatchEvent(new Event("input", { bubbles: true }));
          }
          if (email) {
            email.value = me?.email ?? "";
            email.dispatchEvent(new Event("input", { bubbles: true }));
          }

          // Style all text inputs, email inputs, and textareas
          const textInputs = form.querySelectorAll<HTMLInputElement>(
            'input[type="text"], input[type="email"], input[type="tel"], input[type="number"], textarea'
          );
          textInputs.forEach((input) => {
            input.style.backgroundColor = card || background;
            input.style.color = foreground;
            input.style.borderColor = border;
            input.style.padding = "8px 12px";
            input.style.fontSize = "14px";
            input.style.transition = "border-color 0.2s ease";

            // Focus styles
            input.addEventListener("focus", () => {
              input.style.borderColor = inputFocused;
              input.style.outline = "none";
              input.style.boxShadow = `0 0 0 2px ${inputFocused}33`;
            });

            input.addEventListener("blur", () => {
              input.style.borderColor = border;
              input.style.boxShadow = "none";
            });

            // Placeholder color
            input.style.setProperty("--placeholder-color", mutedForeground);
            const style = document.createElement("style");
            style.textContent = `
              input::placeholder,
              textarea::placeholder {
                color: ${mutedForeground} !important;
                opacity: 1;
              }
            `;
            if (!document.head.querySelector("#hubspot-placeholder-styles")) {
              style.id = "hubspot-placeholder-styles";
              document.head.appendChild(style);
            }
          });

          // Style labels
          const labels = form.querySelectorAll<HTMLLabelElement>(
            "label:not(.hs-error-msg)"
          );
          labels.forEach((label) => {
            label.style.color = foreground;
            label.style.fontSize = "14px";
            label.style.fontWeight = "500";
            label.style.marginBottom = "4px";
          });

          // Style required asterisks
          const requiredAsterisks =
            form.querySelectorAll<HTMLElement>(".hs-form-required");
          requiredAsterisks.forEach((asterisk) => {
            asterisk.style.color = destructive;
          });

          // Style error messages
          const errorLabels = form.querySelectorAll<HTMLLabelElement>(
            ".hs-error-msgs label"
          );
          errorLabels.forEach((errorLabel) => {
            errorLabel.style.color = destructive;
            errorLabel.style.fontSize = "12px";
            errorLabel.style.marginTop = "4px";
          });

          // Style error inputs
          const errorInputs = form.querySelectorAll<HTMLInputElement>(
            ".hs-input.error, .field.error input, .field.error textarea"
          );
          errorInputs.forEach((errorInput) => {
            errorInput.style.borderColor = destructive;
          });

          // Style checkboxes and radio buttons
          const checkboxes = form.querySelectorAll<HTMLInputElement>(
            'input[type="checkbox"], input[type="radio"]'
          );
          checkboxes.forEach((checkbox) => {
            checkbox.style.accentColor = primary;
            checkbox.style.width = "auto";
            checkbox.style.height = "auto";
            checkbox.style.marginRight = "8px";
          });

          // Style file inputs
          const fileInputs =
            form.querySelectorAll<HTMLInputElement>('input[type="file"]');
          fileInputs.forEach((fileInput) => {
            fileInput.style.backgroundColor = card || background;
            fileInput.style.borderColor = border;
            fileInput.style.color = foreground;
            fileInput.style.padding = "8px";
          });

          // Style submit button container - remove margins
          const submitContainer = form.querySelector<HTMLElement>(
            ".hs_submit, .hs-submit"
          );
          if (submitContainer) {
            submitContainer.style.margin = "0";
            submitContainer.style.marginTop = "0";
            submitContainer.style.marginBottom = "0";
          }

          const actionsContainer = form.querySelector<HTMLElement>(".actions");
          if (actionsContainer) {
            actionsContainer.style.margin = "0";
            actionsContainer.style.marginTop = "0";
            actionsContainer.style.marginBottom = "0";
            actionsContainer.style.padding = "0";
          }

          // Style submit button - apply immediately to prevent flash
          const submitButton = form.querySelector<HTMLInputElement>(
            'input[type="submit"].hs-button'
          );
          if (submitButton) {
            // Apply styles immediately without transition to prevent color flash
            submitButton.style.transition = "none";
            submitButton.style.background = primary;
            submitButton.style.color = primaryForeground;
            submitButton.style.borderColor = "transparent";
            submitButton.style.fontSize = "14px";
            submitButton.style.fontWeight = "500";
            submitButton.style.height = "36px";
            submitButton.style.cursor = "pointer";
            submitButton.style.margin = "0";

            // Re-enable transitions after a brief delay
            requestAnimationFrame(() => {
              submitButton.style.transition = "all 0.15s ease";
            });

            submitButton.classList.add(
              "bg-primary",
              "text-primary-foreground",
              "hover:bg-primary/90",
              "border",
              "border-transparent",
              "rounded-md",
              "px-4",
              "py-2",
              "transition-colors",
              "duration-150",
              "shadow-sm"
            );

            // Hover effect
            submitButton.addEventListener("mouseenter", () => {
              submitButton.style.opacity = "0.9";
            });
            submitButton.addEventListener("mouseleave", () => {
              submitButton.style.opacity = "1";
            });
          }

          // Style field descriptions
          const fieldDescs =
            form.querySelectorAll<HTMLElement>(".hs-field-desc");
          fieldDescs.forEach((desc) => {
            desc.style.color = mutedForeground;
            desc.style.fontSize = "12px";
            desc.style.marginBottom = "4px";
          });

          // Style form container background
          const formContainer = form.closest(
            ".hbspt-form"
          ) as HTMLElement | null;
          if (formContainer) {
            formContainer.style.backgroundColor = background || "transparent";
          }

          // Hide loading and show form container after all styling is complete
          // First, immediately hide loading text with CSS to prevent overlap
          if (loadingRef.current) {
            loadingRef.current.style.display = "none";
          }

          // Update React state
          setIsLoading(false);

          // Wait for next frame to ensure loading text is removed from DOM
          requestAnimationFrame(() => {
            if (formRef.current) {
              formRef.current.style.display = "block";
            }
          });
        },
      });

      formCreatedRef.current = true;
    } catch (err) {
      setError(
        "Der opstod en fejl ved oprettelse af formular. Prøv venligst igen senere."
      );
      setIsLoading(false);
      logger.error("Failed to create HubSpot form", {
        operation: "createHubSpotForm",
        error: err,
      });
    }
  }, [me]);

  useEffect(() => {
    // Check if script is already loaded
    if (window.hbspt?.forms?.create) {
      createForm();
      return;
    }

    // Check if script is already in the DOM
    const existingScript = document.querySelector(
      `script[src="${HUBSPOT_SCRIPT_URL}"]`
    );

    if (existingScript) {
      // Wait for script to load
      existingScript.addEventListener("load", createForm);
      return () => {
        existingScript.removeEventListener("load", createForm);
      };
    }

    // Load the script
    const script = document.createElement("script");
    script.src = HUBSPOT_SCRIPT_URL;
    script.charset = "utf-8";
    script.type = "text/javascript";
    script.async = true;

    script.onload = () => {
      createForm();
    };

    script.onerror = () => {
      setError(
        "Kunne ikke indlæse HubSpot formular. Prøv venligst igen senere."
      );
      setIsLoading(false);
    };

    document.body.appendChild(script);

    return () => {
      // Cleanup: remove script if component unmounts before it loads
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [createForm]);

  return (
    <div className="w-full">
      {isLoading && (
        <div ref={loadingRef} className="flex items-center justify-center py-8">
          <p className="text-muted-foreground">Indlæser formular...</p>
        </div>
      )}
      {error && (
        <div className="flex items-center justify-center py-8">
          <p className="text-destructive">{error}</p>
        </div>
      )}
      <div id="hubspot-form-container" ref={formRef} className="w-full" />
    </div>
  );
};

export default HubSpotForm;
